import { useLayoutEffect, useSyncExternalStore } from 'react';
import { usePaulinaShouldYield } from '@components/AgentChat/usePaulinaShouldYield';

/**
 * OverlayGate — single coordination point for every interruptive overlay (PWA install prompt,
 * notification permission prompt, announcement modals, ...). It gives two guarantees that each
 * surface used to ignore on its own:
 *
 *   1. Operational UI wins: gated overlays yield while an incoming order is on screen. The signal
 *      comes from the project's single source of truth (`usePaulinaShouldYield`), bridged into the
 *      store by <OverlayGateBridge/>, so the rule never diverges across surfaces.
 *   2. One at a time: among eligible overlays only the highest-priority one is shown, so they no
 *      longer stack. Ties break by claim order (first claim wins).
 *
 * Implemented as a module singleton + `useSyncExternalStore` (same pattern as the rollout store) so
 * no provider has to wrap the tree. Blocking surfaces opt in with `useOverlaySlot(id, priority,
 * eligible)` and render only when granted; passive surfaces (banners, cards) use
 * `useOperationalYield()` to hide during operations.
 */

export const OVERLAY_PRIORITY = {
  /** Mis Zonas fullscreen map takeover (position:fixed, z-index 200 — above the whole app,
      including the announcement modal at z-110). It claims a slot while open so no other gated
      overlay is GRANTED invisible underneath the map; it defers those until the user exits. It
      still yields to an incoming order by UNMOUNTING (exitFull on operational yield), so the
      operational invariant holds. Highest priority: nothing should render over the takeover. */
  misZonasFullscreen: 40,
  /** Backend-curated announcement modals — most important interruptive surface. */
  announcementModal: 30,
  /** Feature-rollout invitations (e.g. "try the new menu"). Above OS nags so an experiment
      invite outranks an install/permission nag, but NOT counted against the per-session OS-nag
      cap below — suppressing it would distort the rollout experiment's exposure. */
  featureInvitation: 20,
  /** Notification permission — an OS nag, but more useful than install (it powers order alerts),
      so it outranks the PWA prompt for the single per-session OS-nag slot. */
  notifPermission: 15,
  /** OS/system nags: PWA install — least urgent. */
  systemPrompt: 10,
} as const;

// An "OS nag" = PWA install / notification permission. We show at most ONE of these per session
// so a first-time user isn't asked to install AND enable notifications back to back. Anything
// above this ceiling (announcements, feature invitations) is never capped.
const OS_NAG_CEILING = OVERLAY_PRIORITY.notifPermission;
const isOsNag = (priority: number) => priority <= OS_NAG_CEILING;

interface Claim {
  priority: number;
  seq: number;
}

const claims = new Map<string, Claim>();
let yielding = false;
let seq = 0;
let activeId: string | null = null;
// The first OS nag granted this session. Once set, every OTHER OS nag is suppressed for the rest
// of the session (module-memory, intentionally surviving logout so re-login doesn't re-nag).
let shownOsNagId: string | null = null;
const listeners = new Set<() => void>();

const recompute = () => {
  if (yielding) {
    activeId = null;
    return;
  }
  let winnerId: string | null = null;
  let winner: Claim | null = null;
  claims.forEach((c, id) => {
    // Anti-spam: once an OS nag has been dismissed this session (see `release`), the others can
    // never win. Locking on dismissal — not on first paint — avoids a mount-order race where the
    // first OS nag to claim would lock the slot before a higher-priority OS nag mounts.
    if (isOsNag(c.priority) && shownOsNagId !== null && id !== shownOsNagId) return;
    if (!winner || c.priority > winner.priority || (c.priority === winner.priority && c.seq < winner.seq)) {
      winner = c;
      winnerId = id;
    }
  });
  activeId = winnerId;
};

const emit = () => listeners.forEach((l) => l());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getActiveId = () => activeId;
const getYielding = () => yielding;

const claim = (id: string, priority: number) => {
  const current = claims.get(id);
  if (current && current.priority === priority) return;
  seq += 1;
  claims.set(id, { priority, seq });
  recompute();
  emit();
};

const release = (id: string) => {
  const released = claims.get(id);
  if (!released) return;
  const wasActive = activeId === id;
  claims.delete(id);
  // Anti-spam: if the user dismissed (or navigated away from) an OS nag that was on screen, count
  // it as "nagged this session" so no other OS nag (PWA/notif) shows until the next session.
  if (wasActive && shownOsNagId === null && isOsNag(released.priority)) {
    shownOsNagId = id;
  }
  recompute();
  emit();
};

const setYielding = (next: boolean) => {
  if (yielding === next) return;
  yielding = next;
  recompute();
  emit();
};

// Claims a slot for a blocking/interruptive overlay. Returns whether THIS overlay may render now:
// true only when it is eligible, no higher-priority overlay is claiming, and no operation is in progress.
export const useOverlaySlot = (id: string, priority: number, eligible: boolean): boolean => {
  // useLayoutEffect (not useEffect): claim/release runs synchronously before paint, so the slot
  // is claimed before the browser paints and the gated overlay never flashes hidden on first render.
  useLayoutEffect(() => {
    if (eligible) claim(id, priority);
    else release(id);
    return () => release(id);
  }, [id, priority, eligible]);

  const current = useSyncExternalStore(subscribe, getActiveId);
  return current === id;
};

// True while operational UI (an incoming order) should suppress passive overlays (banners, cards).
export const useOperationalYield = (): boolean => useSyncExternalStore(subscribe, getYielding);

// Bridges the React-context operational signal (`usePaulinaShouldYield`) into the module store.
// Mount once, anywhere inside NotificationProvider — it renders nothing.
export const OverlayGateBridge = (): null => {
  const { hayPedido } = usePaulinaShouldYield();
  // useLayoutEffect (matching useOverlaySlot): apply the yield synchronously before paint, so an
  // overlay never flashes for one frame when an incoming order should already be hiding it.
  useLayoutEffect(() => {
    setYielding(hayPedido);
  }, [hayPedido]);
  return null;
};

// Test-only: clears all module-singleton state between tests. Not for production use.
export const __resetOverlayGateForTests = (): void => {
  claims.clear();
  yielding = false;
  seq = 0;
  activeId = null;
  shownOsNagId = null;
  emit();
};

// Test-only: drives the operational-yield signal without mounting <OverlayGateBridge/>.
export const __setYieldingForTests = (next: boolean): void => setYielding(next);
